import React, { useState } from 'react';
import styled from 'styled-components';
import { FiArrowDown, FiArrowUp, FiClock, FiExternalLink, FiPlus, FiSearch, FiShield, FiUsers } from 'react-icons/fi';
import type { Clan, ClanInvitation } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFormat } from '../../hooks/useFormat';
import { levelFromElo } from '../../lib/authUser';
import { Action, Field, Select } from '../MVP/shared';
import { StateBlock, Skeleton } from '../../components/UI';
import { monogram } from '../MVP/home/primitives';

/* ───────────────────────── общие кирпичи ───────────────────────── */
const Mark = styled.span<{ $size?: number }>`
  flex: 0 0 ${(p) => p.$size ?? 48}px;
  width: ${(p) => p.$size ?? 48}px;
  height: ${(p) => p.$size ?? 48}px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.surfaceHover};
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: ${(p) => Math.round((p.$size ?? 48) * 0.32)}px;
  font-weight: 700;
  letter-spacing: 0.04em;
`;
const Surface = styled.section`
  min-width: 0;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  @media (max-width: 767px) {
    padding: 16px;
  }
`;
const Empty = styled.div`
  display: grid;
  justify-items: start;
  gap: 8px;
  padding: 32px 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  svg {
    width: 28px;
    height: 28px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  h2 {
    font-size: 20px;
    line-height: 28px;
  }
  p {
    max-width: 52ch;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }
`;
const Meta = styled.p`
  display: flex;
  flex-wrap: wrap;
  gap: 2px 14px;
  font-size: 14px;
  line-height: 20px;
  color: ${(p) => p.theme.colors.textSecondary};
  span {
    white-space: nowrap;
  }
`;
const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  svg {
    width: 14px;
    height: 14px;
  }
`;

const reqText = (t: (k: string, p?: Record<string, string | number>) => string, c: Clan) => {
  const r = c.requirements;
  if (!r) return '';
  const parts: string[] = [];
  if (r.minLevel > 1) parts.push(t('cl.req_level', { n: r.minLevel }));
  if (r.minElo > 0) parts.push(t('cl.req_elo', { n: r.minElo }));
  if (r.minWinRate > 0) parts.push(t('cl.req_winrate', { n: r.minWinRate }));
  if (r.requiredGames > 0) parts.push(t('cl.req_games', { n: r.requiredGames }));
  return parts.join(', ');
};
const members = (c: Clan) => (typeof c.memberCount === 'number' ? c.memberCount : c.members.length);

/* ───────────────────────── обзор ───────────────────────── */
const Toolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 200px auto;
  gap: 8px;
  align-items: center;
  .search {
    position: relative;
    > svg {
      position: absolute;
      left: 14px;
      top: 50%;
      width: 18px;
      height: 18px;
      transform: translateY(-50%);
      color: ${(p) => p.theme.colors.textMuted};
      pointer-events: none;
    }
    input {
      padding-left: 42px;
    }
  }
  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr) auto;
    .search {
      grid-column: 1 / -1;
    }
  }
`;
const Grid = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr));
  gap: 16px;
`;
const Card = styled.li`
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  gap: 12px;
  min-width: 0;
  padding: 20px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.surface};
  transition: background ${(p) => p.theme.motion.fast};
  &:hover {
    background: ${(p) => p.theme.colors.raised};
  }
  .head {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  h3 {
    font-size: 17px;
    line-height: 24px;
    font-weight: 650;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .tag {
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .desc {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
    font-size: 14px;
    line-height: 20px;
    color: ${(p) => p.theme.colors.textSecondary};
    min-height: 40px;
  }
  .join {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
`;

export function ClanOverview({
  clans,
  query,
  onQuery,
  sortBy,
  onSortBy,
  sortOrder,
  onSortOrder,
  myClan,
  pendingIds,
  joiningId,
  empty,
  status,
  onRetry,
  onView,
  onJoin,
  onCreate,
}: {
  clans: Clan[];
  query: string;
  onQuery: (v: string) => void;
  sortBy: string;
  onSortBy: (v: any) => void;
  sortOrder: 'asc' | 'desc';
  onSortOrder: (v: 'asc' | 'desc') => void;
  myClan: Clan | null;
  pendingIds: string[];
  joiningId: string;
  /** Каталог пуст вообще (не только по текущему поиску). */
  empty: boolean;
  /** Состояние загрузки каталога: пустой список и сбой запроса не должны выглядеть одинаково. */
  status: 'loading' | 'ready' | 'error';
  onRetry: () => void;
  onView: (id: string, el?: HTMLElement | null) => void;
  onJoin: (id: string) => void;
  onCreate: () => void;
}) {
  const { t } = useLanguage();
  const f = useFormat();
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Toolbar>
        <label className="search">
          <FiSearch aria-hidden="true" />
          <Field type="search" aria-label={t('clans.search')} placeholder={t('clans.search')} value={query} onChange={(e) => onQuery(e.target.value)} />
        </label>
        <Select aria-label={t('clans.sort_by')} value={sortBy} onChange={(e) => onSortBy(e.target.value)}>
          <option value="name">{t('clans.sort.name')}</option>
          <option value="members">{t('clans.sort.members')}</option>
          <option value="winRate">{t('clans.sort.winRate')}</option>
        </Select>
        <Action
          type="button"
          aria-label={sortOrder === 'asc' ? t('clans.order.asc') : t('clans.order.desc')}
          title={sortOrder === 'asc' ? t('clans.order.asc') : t('clans.order.desc')}
          onClick={() => onSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          style={{ width: 48, padding: 0 }}
        >
          {sortOrder === 'asc' ? <FiArrowUp aria-hidden="true" /> : <FiArrowDown aria-hidden="true" />}
        </Action>
      </Toolbar>

      {status === 'loading' && clans.length === 0 ? (
        <div role="status" aria-label={t('common.loading')} style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} $h={168} $r={16} />
          ))}
        </div>
      ) : status === 'error' && clans.length === 0 ? (
        <StateBlock kind="error" title={t('cl.dir_error')} text={t('sys.temporary')} actionLabel={t('common.retry')} onAction={onRetry} />
      ) : clans.length === 0 ? (
        <Empty>
          <FiUsers aria-hidden="true" />
          <h2>{empty ? t('cl.empty_dir') : t('cl.empty_find')}</h2>
          <p>{empty ? t('cl.empty_dir_text') : t('cl.empty_find_text')}</p>
          <div className="actions">
            {!empty && query && (
              <Action type="button" onClick={() => onQuery('')}>
                {t('cl.search_clear')}
              </Action>
            )}
            {empty && !myClan && (
              <Action type="button" $variant="primary" onClick={onCreate}>
                <FiPlus aria-hidden="true" />
                {t('cl.create')}
              </Action>
            )}
          </div>
        </Empty>
      ) : (
        <>
          <p className="sr-only" aria-live="polite">
            {t('clans.results', { n: clans.length })}
          </p>
          <Grid>
            {clans.map((c) => {
              const full = members(c) >= c.maxMembers;
              const mine = myClan?.id === c.id;
              const pending = pendingIds.includes(c.id);
              const approval = !c.settings?.autoAccept;
              const reqs = reqText(t, c);
              return (
                <Card key={c.id}>
                  <div className="head">
                    <Mark aria-hidden="true">{monogram(c.tag)}</Mark>
                    <div style={{ minWidth: 0 }}>
                      <h3 title={c.name}>{c.name}</h3>
                      <span className="tag">[{c.tag}]</span>
                    </div>
                  </div>
                  <Meta>
                    <span>
                      {t('cl.members_n', { n: members(c), max: c.maxMembers })} {t('cl.members_label')}
                    </span>
                    <span>
                      {f.percent(c.stats.winRate, 0)} {t('cl.winrate')}
                    </span>
                    <span>{t('cl.avg_elo', { n: c.stats.averageElo })}</span>
                  </Meta>
                  <div>
                    <p className="desc">{c.description || t('cl.no_desc')}</p>
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <p className="join">
                      {!c.isPublic ? (
                        <>
                          <FiShield aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 6 }} />
                          {t('cl.invite_only')}
                        </>
                      ) : approval ? (
                        t('cl.by_request')
                      ) : (
                        t('cl.free_join')
                      )}
                      {reqs && ` · ${reqs}`}
                    </p>
                    <div className="actions">
                      <Action type="button" $variant="ghost" onClick={(e) => onView(c.id, e.currentTarget)} style={{ marginLeft: -12 }}>
                        {t('cl.details')}
                      </Action>
                      {mine ? (
                        <Status>{t('cl.your_clan')}</Status>
                      ) : myClan ? null : pending ? (
                        <Status>
                          <FiClock aria-hidden="true" />
                          {t('cl.applied')}
                        </Status>
                      ) : !c.isPublic ? null : full ? (
                        <Status>{t('cl.full')}</Status>
                      ) : (
                        <Action type="button" $variant="primary" disabled={!!joiningId} aria-busy={joiningId === c.id} onClick={() => onJoin(c.id)}>
                          {approval ? t('cl.apply') : t('cl.join')}
                        </Action>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </Grid>
        </>
      )}
    </div>
  );
}

/* ───────────────────────── мой клан ───────────────────────── */
const Hero = styled(Surface)`
  display: grid;
  gap: 16px;
  .top {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
  }
  h2 {
    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
    letter-spacing: -0.01em;
    overflow-wrap: anywhere;
  }
  .sub {
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .desc {
    max-width: 70ch;
    overflow-wrap: anywhere;
    color: ${(p) => p.theme.colors.textSecondary};
    line-height: 22px;
  }
  .actions {
    margin-left: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  @media (max-width: 640px) {
    .actions {
      margin-left: 0;
      width: 100%;
    }
  }
`;
const Two = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;
const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-bottom: 12px;
  h3 {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
  }
`;
const Rows = styled.ul`
  list-style: none;
  display: grid;
  gap: 2px;
  margin: 0 -8px;
  li {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    min-height: 60px;
    padding: 8px;
    border-radius: 10px;
    transition: background ${(p) => p.theme.motion.fast};
    &:hover {
      background: ${(p) => p.theme.colors.raised};
    }
  }
  .nick {
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
    overflow-wrap: break-word;
    em {
      margin-left: 8px;
      padding: 0 6px;
      border-radius: 6px;
      background: ${(p) => p.theme.colors.surfaceHover};
      color: ${(p) => p.theme.colors.textSecondary};
      font-size: 11px;
      font-style: normal;
      line-height: 18px;
      vertical-align: 1px;
    }
  }
  .sub {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .leader {
    color: ${(p) => p.theme.colors.primary};
    font-weight: 600;
  }
  .acts {
    display: flex;
    gap: 8px;
  }
  /* На узком экране действия уходят под текст: ник не должен ломаться по буквам. */
  @media (max-width: 560px) {
    li {
      grid-template-columns: auto minmax(0, 1fr);
    }
    li > .acts {
      grid-column: 1 / -1;
    }
  }
`;
const Facts = styled.dl`
  display: grid;
  gap: 16px;
  dt {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  dd {
    font-size: 24px;
    line-height: 30px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  dd.text {
    font-size: 15px;
    line-height: 22px;
    font-weight: 500;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }
`;
const Invite = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  margin-bottom: 16px;
  @media (max-width: 480px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Links = styled.p`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    font-weight: 600;
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;
const Rules = styled.div`
  white-space: pre-line;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 22px;
  color: ${(p) => p.theme.colors.textSecondary};
`;

export function ClanInfoPanel({ clan }: { clan: Clan }) {
  const { t } = useLanguage();
  const f = useFormat();
  const s = clan.settings;
  const reqs = reqText(t, clan);
  return (
    <Surface style={{ display: 'grid', gap: 20 }}>
      <Facts>
        <div className="grid">
          <div>
            <dt>{t('cl.stat_members')}</dt>
            <dd>
              {members(clan)}
              <span style={{ fontSize: 14, fontWeight: 500 }}> / {clan.maxMembers}</span>
            </dd>
          </div>
          <div>
            <dt>{t('cl.stat_winrate')}</dt>
            <dd>{f.percent(clan.stats.winRate, 0)}</dd>
          </div>
          <div>
            <dt>{t('cl.stat_elo')}</dt>
            <dd>{f.number(clan.stats.averageElo, 0)}</dd>
          </div>
          <div>
            <dt>{t('cl.stat_matches')}</dt>
            <dd>{f.number(clan.stats.totalMatches, 0)}</dd>
          </div>
        </div>
        <div>
          <dt>{t('cl.rules_join')}</dt>
          <dd className="text">
            {!clan.isPublic ? t('cl.invite_only') : s?.autoAccept ? t('cl.free_join') : t('cl.by_request')}
            <br />
            {reqs || t('cl.req_none')}
          </dd>
        </div>
      </Facts>
      {(s?.discordLink || s?.website) && (
        <div>
          <Head style={{ marginBottom: 4 }}>
            <h3 style={{ fontSize: 15 }}>{t('cl.links')}</h3>
          </Head>
          <Links>
            {s?.discordLink && (
              <a href={s.discordLink} target="_blank" rel="noopener noreferrer">
                {t('cl.open_link_discord')}
                <FiExternalLink aria-hidden="true" />
              </a>
            )}
            {s?.website && (
              <a href={s.website} target="_blank" rel="noopener noreferrer">
                {t('cl.open_link_site')}
                <FiExternalLink aria-hidden="true" />
              </a>
            )}
          </Links>
        </div>
      )}
      {s?.rules && (
        <div>
          <Head style={{ marginBottom: 4 }}>
            <h3 style={{ fontSize: 15 }}>{t('cl.rules')}</h3>
          </Head>
          <Rules>{s.rules}</Rules>
        </div>
      )}
    </Surface>
  );
}

export function MyClanView({
  clan,
  role,
  userId,
  joinRequests,
  inviteLogin,
  onInviteLogin,
  onInvite,
  onAcceptJoin,
  onDeclineJoin,
  onKick,
  onEdit,
  onLeave,
  onDisband,
}: {
  clan: Clan;
  role: string | null;
  userId?: string;
  joinRequests: Array<{ id: string; username: string; elo: number; message: string }>;
  inviteLogin: string;
  onInviteLogin: (v: string) => void;
  onInvite: () => void;
  onAcceptJoin: (id: string) => Promise<unknown> | void;
  onDeclineJoin: (id: string) => Promise<unknown> | void;
  onKick: (id: string, name: string) => void;
  onEdit: () => void;
  onLeave: () => void;
  onDisband: () => void;
}) {
  const { t } = useLanguage();
  const f = useFormat();
  const leader = role === 'leader';
  const [busy, setBusy] = useState<string>('');
  const run = async (id: string, fn: (id: string) => Promise<unknown> | void) => {
    if (busy) return;
    setBusy(id);
    try {
      await fn(id);
    } finally {
      setBusy('');
    }
  };
  const canInvite = leader || clan.settings?.allowMemberInvites;

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <Hero aria-labelledby="my-clan-name">
        <div className="top">
          <Mark $size={64} aria-hidden="true">
            {monogram(clan.tag)}
          </Mark>
          <div style={{ minWidth: 0 }}>
            <h2 id="my-clan-name">{clan.name}</h2>
            <p className="sub">
              [{clan.tag}] · {leader ? t('cl.role_leader') : t('cl.role_member')} · {t('cl.created', { date: f.date(clan.createdAt) })}
            </p>
          </div>
          <div className="actions">
            {leader && (
              <Action type="button" onClick={onEdit}>
                {t('cl.edit')}
              </Action>
            )}
            <Action type="button" $variant="danger" onClick={leader ? onDisband : onLeave}>
              {leader ? t('cl.disband') : t('cl.leave')}
            </Action>
          </div>
        </div>
        {clan.description && <p className="desc">{clan.description}</p>}
      </Hero>

      <Two>
        <div style={{ display: 'grid', gap: 24, minWidth: 0 }}>
          {leader && (
            <Surface aria-labelledby="clan-requests">
              <Head>
                <h3 id="clan-requests">
                  {t('cl.requests')} · {joinRequests.length}
                </h3>
              </Head>
              {joinRequests.length === 0 ? (
                <p style={{ color: 'inherit', opacity: 0.7 }}>{t('cl.requests_none')}</p>
              ) : (
                <Rows>
                  {joinRequests.map((jr) => (
                    <li key={jr.id}>
                      <Mark $size={40} aria-hidden="true">
                        {monogram(jr.username)}
                      </Mark>
                      <div style={{ minWidth: 0 }}>
                        <div className="nick">{jr.username}</div>
                        <div className="sub">
                          ELO {jr.elo}
                          {jr.message ? ` · «${jr.message}»` : ''}
                        </div>
                      </div>
                      <div className="acts">
                        <Action type="button" $variant="primary" disabled={busy === jr.id} onClick={() => run(jr.id, onAcceptJoin)} aria-label={`${t('cl.accept')}: ${jr.username}`}>
                          {t('cl.accept')}
                        </Action>
                        <Action type="button" $variant="ghost" disabled={busy === jr.id} onClick={() => run(jr.id, onDeclineJoin)} aria-label={`${t('cl.decline')}: ${jr.username}`}>
                          {t('cl.decline')}
                        </Action>
                      </div>
                    </li>
                  ))}
                </Rows>
              )}
            </Surface>
          )}

          <Surface aria-labelledby="clan-members">
            <Head>
              <h3 id="clan-members">
                {t('cl.members')} · {clan.members.length}
              </h3>
            </Head>
            {canInvite && (
              <Invite
                onSubmit={(e) => {
                  e.preventDefault();
                  onInvite();
                }}
              >
                <Field aria-label={t('cl.invite_title')} placeholder={t('cl.invite_placeholder')} value={inviteLogin} onChange={(e) => onInviteLogin(e.target.value)} />
                <Action type="submit" $variant="primary">
                  <FiPlus aria-hidden="true" />
                  {t('cl.invite')}
                </Action>
              </Invite>
            )}
            <Rows>
              {clan.members.map((m) => (
                <li key={m.user.id}>
                  <Mark $size={40} aria-hidden="true">
                    {monogram(m.user.username)}
                  </Mark>
                  <div style={{ minWidth: 0 }}>
                    <div className="nick">
                      {m.user.username}
                      {m.user.id === userId && <em>{t('cl.you')}</em>}
                    </div>
                    <div className="sub">
                      <span className={m.role === 'leader' ? 'leader' : undefined}>{m.role === 'leader' ? t('cl.role_leader') : t('cl.role_member')}</span>
                      {' · '}
                      {t('cl.level_short', { n: levelFromElo(m.user.elo) })} · {m.user.elo} ELO
                    </div>
                  </div>
                  <div className="acts">
                    {leader && m.role !== 'leader' && (
                      <Action type="button" $variant="ghost" onClick={() => onKick(m.user.id, m.user.username)} aria-label={`${t('cl.kick')}: ${m.user.username}`}>
                        {t('cl.kick')}
                      </Action>
                    )}
                  </div>
                </li>
              ))}
            </Rows>
          </Surface>
        </div>
        <ClanInfoPanel clan={clan} />
      </Two>
    </div>
  );
}

export function NoClan({ onFind, onCreate }: { onFind: () => void; onCreate: () => void }) {
  const { t } = useLanguage();
  return (
    <Empty>
      <FiUsers aria-hidden="true" />
      <h2>{t('cl.no_clan')}</h2>
      <p>{t('cl.no_clan_text')}</p>
      <div className="actions">
        <Action type="button" $variant="primary" onClick={onFind}>
          {t('cl.find')}
        </Action>
        <Action type="button" onClick={onCreate}>
          <FiPlus aria-hidden="true" />
          {t('cl.create')}
        </Action>
      </div>
    </Empty>
  );
}

/* ───────────────────────── приглашения ───────────────────────── */
const InvList = styled.ul`
  list-style: none;
  display: grid;
  gap: 12px;
  li {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px 16px;
    padding: 16px 20px;
    border-radius: 12px;
    background: ${(p) => p.theme.colors.surface};
  }
  h3 {
    font-size: 17px;
    line-height: 24px;
    font-weight: 650;
    overflow-wrap: anywhere;
  }
  .msg {
    margin-top: 4px;
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
    overflow-wrap: anywhere;
  }
  .acts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  @media (max-width: 700px) {
    li {
      grid-template-columns: auto minmax(0, 1fr);
    }
    .acts {
      grid-column: 1 / -1;
    }
  }
`;

export function InvitationsView({
  invitations,
  onAccept,
  onDecline,
  onView,
  onOverview,
}: {
  invitations: ClanInvitation[];
  onAccept: (id: string) => Promise<unknown> | void;
  onDecline: (id: string) => Promise<unknown> | void;
  onView: (clan: Clan) => void;
  onOverview: () => void;
}) {
  const { t } = useLanguage();
  const f = useFormat();
  const [busy, setBusy] = useState('');
  const run = async (id: string, fn: (id: string) => Promise<unknown> | void) => {
    if (busy) return;
    setBusy(id);
    try {
      await fn(id);
    } finally {
      setBusy('');
    }
  };
  if (invitations.length === 0)
    return (
      <Empty>
        <FiClock aria-hidden="true" />
        <h2>{t('cl.inv_none')}</h2>
        <p>{t('cl.inv_none_text')}</p>
        <div className="actions">
          <Action type="button" onClick={onOverview}>
            {t('cl.to_overview')}
          </Action>
        </div>
      </Empty>
    );
  return (
    <InvList>
      {invitations.map((inv) => {
        const expired = inv.status === 'expired' || (inv.expiresAt && Date.parse(inv.expiresAt) < Date.now());
        return (
          <li key={inv.id}>
            <Mark aria-hidden="true">{monogram(inv.clan.tag)}</Mark>
            <div style={{ minWidth: 0 }}>
              <h3>{inv.clan.name}</h3>
              <Meta>
                <span>{t('cl.inv_from', { name: inv.invitedBy.username })}</span>
                <span>
                  {t('cl.members_n', { n: members(inv.clan), max: inv.clan.maxMembers })} {t('cl.members_label')}
                </span>
                <span>{expired ? t('cl.inv_expired') : inv.expiresAt ? t('cl.inv_expires', { date: f.date(inv.expiresAt) }) : ''}</span>
              </Meta>
              {inv.message && <p className="msg">«{inv.message}»</p>}
            </div>
            <div className="acts">
              <Action type="button" $variant="primary" disabled={!!expired || busy === inv.id} onClick={() => run(inv.id, onAccept)} aria-label={`${t('cl.accept')}: ${inv.clan.name}`}>
                {t('cl.accept')}
              </Action>
              <Action type="button" $variant="ghost" disabled={busy === inv.id} onClick={() => run(inv.id, onDecline)} aria-label={`${t('cl.decline')}: ${inv.clan.name}`}>
                {t('cl.decline')}
              </Action>
              <Action type="button" $variant="ghost" onClick={() => onView(inv.clan)}>
                {t('cl.details')}
              </Action>
            </div>
          </li>
        );
      })}
    </InvList>
  );
}

/* ───────────────────────── детали клана (диалог) ───────────────────────── */
export function ClanDetailsBody({ clan }: { clan: Clan }) {
  const { t } = useLanguage();
  const f = useFormat();
  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
        <Mark $size={56} aria-hidden="true">
          {monogram(clan.tag)}
        </Mark>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 22, lineHeight: '30px', overflowWrap: 'anywhere' }}>{clan.name}</h2>
          <p style={{ fontSize: 14, opacity: 0.75 }}>
            [{clan.tag}] · {clan.isPublic ? t('cl.type_public') : t('cl.type_private')} · {t('cl.created', { date: f.date(clan.createdAt) })}
          </p>
        </div>
      </div>
      {clan.description && <p style={{ lineHeight: '22px', overflowWrap: 'anywhere' }}>{clan.description}</p>}
      <p style={{ fontSize: 14, opacity: 0.85 }}>{t('cl.led_by', { name: clan.leader.username })}</p>
      <ClanInfoPanel clan={clan} />
    </div>
  );
}

